import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { getMessageKey } from '../../../../utils/utils';
import { CAMP_TYPE_EO } from '../../CertsListTile/constant';

import {
  EO_TYPE_TITLES_AND_ORDER,
  TYPE_TITLES_AND_ORDER,
} from './CertsListSidePanelBodyCommon/constant';
import { ProgressCard } from './CertsListSidePanelBodyCommon';
import { PROGRESS_KEYS_TO_EXCLUDE } from './constant';
import { customSortByDecision } from './utils';

import './CertsListSidePanelBody.css';

function CertsListSidePanelBodyProgress({ progressData, campaignType }) {
  const [selection, setSelection] = useState(1);
  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);
  const isEOCampaign = campaignType === CAMP_TYPE_EO;
  const byDecisionClasses = classnames(
    'CertsListSidePanelBodyProgress-toggleWrapper--isByDecision',
    selection === 2 && 'CertsListSidePanelBodyProgress-toggleWrapper--isActive'
  );

  const byTypeClasses = classnames(
    'CertsListSidePanelBodyProgress-toggleWrapper--isByType',
    selection === 2 && 'CertsListSidePanelBodyProgress-toggleWrapper--isActive'
  );

  const PROGRESS_LABELS = {
    Done: 'Done.v2.label.ATTESTATIONS',
    'Manager Confirmed': 'ManagerConfirmed.v2.label.ATTESTATIONS',
    ManagerConfirmed: 'ManagerConfirmed.v2.label.ATTESTATIONS',
    Pending: 'Pending.v2.label.ATTESTATIONS',
    Progress: 'Progress.v2.label.ATTESTATIONS',
    Rejected: 'Rejected.v2.label.ATTESTATIONS',
    Total: 'Total.v2.label.ATTESTATIONS',
    'Manager Confirmed With End Date Changed':
      'ManagerConfirmedWithEndDateChanged.v2.label.ATTESTATIONS',
    ManagerConfirmedWithEndDateChanged:
      'ManagerConfirmedWithEndDateChanged.v2.label.ATTESTATIONS',
    Approved: 'Approved.v2.label.ATTESTATIONS',
    'End Date Changed': 'EndDateChanged.v2.label.ATTESTATIONS',
    EndDateChanged: 'EndDateChanged.v2.label.ATTESTATIONS',
    Terminated: 'Terminated.v2.label.ATTESTATIONS',
    NotConfirmedByManager: 'NotConfirmedByManager.v2.label.ATTESTATIONS',
    'Not Confirmed By Manager': 'NotConfirmedByManager.v2.label.ATTESTATIONS',
    ConditionallyCertified: 'ConditionallyCertified.v2.label.ATTESTATIONS',
    'Conditionally Certified': 'ConditionallyCertified.v2.label.ATTESTATIONS',
    'Does Not Belong To Me': 'DoesNotBelongToMe.v2.label.ATTESTATIONS',
    'Does Not Belong': 'DoesNotBelong.v2.label.ATTESTATIONS',
    Decommissioned: 'Decommissioned.v2.label.ATTESTATIONS',
    NoActionRequired: 'NoActionRequired.v2.label.ATTESTATIONS',
  };

  const pendingCount =
    progressData?.type?.totalPending || progressData?.decision?.Pending || 0;
  const totalCount =
    progressData?.type?.total || progressData?.decision?.Total || 0;

  const renderDecisionSubTab = () => {
    if (!progressData?.decision?.Total) {
      return (
        <Box
          tag='div'
          className='CertsListSidePanelBodyProgress-toggleWrapperNoData'>
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color='--color-foreground-secondary-bold'>
            {messages.CERTS_NO_PROGRESS_MSG ||
              localize(intl, getMessageKey('CERTS_NO_PROGRESS_MSG'))}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {progressData?.decision?.Pending > 0 && (
          <Box
            tag='div'
            className={`CertsListSidePanelBodyProgress-toggleWrapperDecisionStatsTitle ${
              progressData?.decision?.Done > 0
                ? 'CertsListSidePanelBodyProgress-toggleWrapperDecisionStatsTitle--isBordered'
                : ''
            }`}>
            <Typography
              kind='body1'
              desktop='body1-d'
              mobile='body1-d'
              color='--color-foreground-secondary-bold'>
              {progressData?.decision?.Pending}
            </Typography>
            <Typography
              kind='body2'
              desktop='body2-d'
              mobile='body2-d'
              color='--color-foreground-secondary-bold'>
              {messages.CERTS_TODO ||
                localize(intl, getMessageKey('CERTS_TODO'))}
            </Typography>
          </Box>
        )}
        {progressData?.decision?.Done > 0 && (
          <Box
            tag='div'
            className='CertsListSidePanelBodyProgress-toggleWrapperDecisionStatsTitle'>
            <Typography
              kind='body1'
              desktop='body1-d'
              mobile='body1-d'
              color='--color-foreground-secondary-bold'>
              {progressData?.decision?.Done}
            </Typography>
            <Typography
              kind='body2'
              desktop='body2-d'
              mobile='body2-d'
              color='--color-foreground-secondary-bold'>
              {messages.CERTS_DONE ||
                localize(intl, getMessageKey('CERTS_DONE'))}
            </Typography>
          </Box>
        )}
        <Box
          tag='div'
          className='CertsListSidePanelBodyProgress-toggleWrapperDecisionStats'>
          {Object.keys(progressData?.decision)
            .filter(
              (decisionKey) => !PROGRESS_KEYS_TO_EXCLUDE.includes(decisionKey)
            )
            .sort(customSortByDecision)
            .map((decisionKey) => (
              <ProgressCard
                cardType={1}
                PROGRESS_LABELS
                action={{
                  title:
                    messages[PROGRESS_LABELS[decisionKey]] ||
                    localize(intl, getMessageKey(PROGRESS_LABELS[decisionKey])),
                }}
                count={progressData?.decision?.[decisionKey]}
              />
            ))}
        </Box>
      </>
    );
  };

  const renderTypeSubTab = () => {
    if (!progressData?.type?.total) {
      return (
        <Box
          tag='div'
          className='CertsListSidePanelBodyProgress-toggleWrapperNoData'>
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color='--color-foreground-secondary-bold'>
            {messages.CERTS_NO_PROGRESS_MSG ||
              localize(intl, getMessageKey('CERTS_NO_PROGRESS_MSG'))}
          </Typography>
        </Box>
      );
    }

    return Object.keys(progressData?.type?.Summary)
      .sort((a, b) =>
        isEOCampaign
          ? EO_TYPE_TITLES_AND_ORDER[a] - EO_TYPE_TITLES_AND_ORDER[b]
          : TYPE_TITLES_AND_ORDER[a] - TYPE_TITLES_AND_ORDER[b]
      )
      .map((typeKey) => {
        const pendingCnt = progressData?.type?.Summary[typeKey]?.Pending || 0;
        const totalCnt = progressData?.type?.Summary[typeKey]?.Total;

        return (
          <ProgressCard
            cardType={2}
            title={typeKey}
            count={totalCnt - pendingCnt}
            totalCount={totalCnt}
          />
        );
      });
  };

  return (
    <>
      <ProgressCard
        cardType={2}
        title='allitems'
        count={totalCount - pendingCount}
        totalCount={totalCount}
      />
      <ToggleButtonGroup
        selection={selection}
        setSelection={setSelection}
        className='CertsListSidePanelBodyProgress-toggleButtonGroup'>
        <ToggleButton
          label={
            messages.CERTS_BY_DECISION ||
            localize(intl, getMessageKey('CERTS_BY_DECISION'))
          }
        />
        <ToggleButton
          label={
            messages.CERTS_BY_TYPE ||
            localize(intl, getMessageKey('CERTS_BY_TYPE'))
          }
        />
      </ToggleButtonGroup>
      <Box tag='div' className='CertsListSidePanelBodyProgress-toggleWrapper'>
        <Box tag='div' className={byDecisionClasses}>
          {renderDecisionSubTab()}
        </Box>
        <Box tag='div' className={byTypeClasses}>
          {renderTypeSubTab()}
        </Box>
      </Box>
    </>
  );
}

CertsListSidePanelBodyProgress.propTypes = {
  progressData: PropTypes.shape({
    decision: PropTypes.shape({
      Pending: PropTypes.number,
      Total: PropTypes.number,
      Done: PropTypes.number,
    }),
    type: PropTypes.shape({
      total: PropTypes.number,
      totalPending: PropTypes.number,
      Summary: PropTypes.shape({}),
    }),
  }),
  campaignType: PropTypes.string,
};

CertsListSidePanelBodyProgress.defaultProps = {
  progressData: {},
  campaignType: '',
};

export default CertsListSidePanelBodyProgress;
